import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Grid
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import {
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { RootState } from '../../store';
import {
  fetchComponentByIdStart,
  createComponentStart,
  updateComponentStart,
  fetchDropdownsStart,
  setSelectedComponent,
  clearComponentError
} from '../../store/reducers/componentSlice';
import { Component, CreateComponentRequest } from '../../services/componentApi';

const useStyles = makeStyles((theme) => ({
  headerContainer: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
  },
  alert: {
    marginBottom: theme.spacing(2),
  },
  paper: {
    padding: theme.spacing(3),
  },
  actionButtons: {
    display: 'flex',
    justifyContent: 'flex-start',
    gap: theme.spacing(2),
    marginTop: theme.spacing(4),
    '& > *': {
      marginRight: theme.spacing(2),
    },
  },
}));

const ComponentEdit: React.FC = () => {
  const classes = useStyles();
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isEditMode = Boolean(id);

  const {
    selectedComponent,
    componentLoading,
    componentError,
    componentValidationErrors,
    dropdownUoMs,
    dropdownsLoading
  } = useSelector((state: RootState) => state.component);

  // Form state
  const [formData, setFormData] = useState<Partial<Component>>({
    Name: '',
    Description: '',
    UoMId: '',
    Value: 0,
    Cost: 0,
    UnitOfMeasurement: '',
    Active: true
  });

  useEffect(() => {
    dispatch(fetchDropdownsStart());
    dispatch(clearComponentError());

    if (isEditMode && id) {
      dispatch(fetchComponentByIdStart(id));
    } else {
      dispatch(setSelectedComponent(null));
    }
  }, [dispatch, id, isEditMode]);

  useEffect(() => {
    if (selectedComponent && isEditMode) {
      setFormData(selectedComponent);
    }
  }, [selectedComponent, isEditMode]);

  const handleInputChange = (field: keyof Component, value: any) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmit = () => {
    const componentData: CreateComponentRequest = {
      Name: formData.Name || '',
      Description: formData.Description || '',
      UoMId: formData.UoMId,
      Value: formData.Value || 0,
      Cost: formData.Cost,
      UnitOfMeasurement: formData.UnitOfMeasurement,
      Active: formData.Active
    };

    if (isEditMode && selectedComponent?.id) {
      dispatch(updateComponentStart({ ...componentData, id: selectedComponent.id }));
    } else {
      dispatch(createComponentStart(componentData));
    }
  };

  const handleCancel = () => {
    navigate('/components');
  };

  if (componentLoading && isEditMode) {
    return (
      <Box p={3}>
        <Typography>Loading component...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" style={{ marginBottom: 24 }}>
        <Typography variant="h4" component="h1">
          {isEditMode ? 'Edit Component' : 'Add New Component'}
        </Typography>
      </Box>

      {componentError && (
        <Alert severity="error" className={classes.alert}>
          {componentError}
        </Alert>
      )}

      {componentValidationErrors.length > 0 && (
        <Alert severity="warning" className={classes.alert}>
          <ul>
            {componentValidationErrors.map((error, index) => (
              <li key={index}>{error}</li>
            ))}
          </ul>
        </Alert>
      )}

      <Paper className={classes.paper}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Component Name *"
              value={formData.Name || ''}
              onChange={(e) => handleInputChange('Name', e.target.value)}
              required
            />
          </Grid>
          
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Unit of Measurement *</InputLabel>
              <Select
                value={formData.UoMId || ''}
                onChange={(e) => handleInputChange('UoMId', e.target.value)}
                label="Unit of Measurement *"
                required
              >
                {dropdownUoMs.map((uom) => (
                  <MenuItem key={uom.Id} value={uom.Id}>
                    {uom.Name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Description *"
              value={formData.Description || ''}
              onChange={(e) => handleInputChange('Description', e.target.value)}
              multiline
              rows={3}
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Value *"
              type="number"
              value={formData.Value || ''}
              onChange={(e) => handleInputChange('Value', parseFloat(e.target.value) || 0)}
              inputProps={{ min: 0, step: 0.01 }}
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Cost"
              type="number"
              value={formData.Cost || ''}
              onChange={(e) => handleInputChange('Cost', parseFloat(e.target.value) || 0)}
              inputProps={{ min: 0, step: 0.01 }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Unit of Measurement"
              value={formData.UnitOfMeasurement || ''}
              onChange={(e) => handleInputChange('UnitOfMeasurement', e.target.value)}
              placeholder="e.g., pieces, kg, meters"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.Active || false}
                  onChange={(e) => handleInputChange('Active', e.target.checked)}
                />
              }
              label="Active"
            />
          </Grid>
        </Grid>

        {/* Action Buttons */}
        <Box display="flex" justifyContent="flex-start" style={{ marginTop: 32 }}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={componentLoading}
            startIcon={<SaveIcon />}
            style={{ marginRight: 16 }}
          >
            {isEditMode ? 'Update' : 'Create'} Component
          </Button>
          
          <Button
            variant="outlined"
            onClick={handleCancel}
            startIcon={<CancelIcon />}
          >
            Cancel
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default ComponentEdit; 